import React, { useState, useEffect, useCallback } from "react";
import { supabase, adminStorage } from "../config";

const STATUS_LABELS = { pending: "Pending", approved: "Approved", rejected: "Rejected" };
const STATUS_STYLES = {
  pending: "bg-yellow-100 text-yellow-700",
  approved: "bg-green-100 text-green-700",
  rejected: "bg-gray-200 text-gray-600",
};

// "A Letter to a Tree" submissions (db/sritaru-letters.sql). Rows come from
// password-gated functions; photos sit in a private bucket and are shown
// through signed links that last an hour. Archive is soft and reversible.
function AdminLetters({ password }) {
  const [rows, setRows] = useState([]);
  const [urls, setUrls] = useState({});
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState("pending"); // pending | approved | rejected | archived
  const [notes, setNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.rpc("sritaru_admin_letters_list", {
        p_password: password,
        p_archived: view === "archived",
      });
      if (error) throw error;
      const list = data || [];
      setRows(list);
      setNotes(Object.fromEntries(list.map((r) => [r.letter_id, r.review_note || ""])));
      if (list.length) {
        const storage = await adminStorage(password);
        const { data: signed, error: signErr } = await storage.createSignedUrls(
          list.flatMap((r) => [r.image_path, r.audio_path].filter(Boolean)),
          3600
        );
        if (signErr) throw signErr;
        setUrls(Object.fromEntries((signed || []).filter((s) => s.signedUrl).map((s) => [s.path, s.signedUrl])));
      }
    } catch (err) {
      alert("Error loading letters: " + err.message);
    } finally {
      setLoading(false);
    }
  }, [password, view]);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id, changes) => {
    const { error } = await supabase.rpc("sritaru_admin_letter_update", {
      p_password: password,
      p_letter_id: id,
      p_changes: changes,
    });
    if (error) return alert("Error saving: " + error.message);
    await load();
  };

  const archive = async (id, on) => {
    let reason = null;
    if (on) {
      reason = window.prompt("Why is this letter being archived? (kept with it; you can restore it later)", "");
      if (reason === null) return;
    }
    const { error } = await supabase.rpc("sritaru_admin_letter_archive", {
      p_password: password,
      p_letter_id: id,
      p_archive: on,
      p_reason: on ? reason.trim() || null : null,
    });
    if (error) return alert("Error: " + error.message);
    await load();
  };

  // Everything make_letter_reel.py needs, as files in Downloads: the photo, the
  // recording and a JSON with the public details plus the donations (no contact details).
  const downloadKit = async (r) => {
    const id = r.letter_id.slice(0, 8);
    const save = (blob, name) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 5000);
    };
    try {
      const ext = (p) => p.split(".").pop();
      const files = { photo: `letter-${id}-photo.${ext(r.image_path)}` };
      if (r.audio_path) files.voice = `letter-${id}-voice.${ext(r.audio_path)}`;
      for (const [key, path] of [["photo", r.image_path], ["voice", r.audio_path]]) {
        if (!path) continue;
        const res = await fetch(urls[path]);
        if (!res.ok) throw new Error(`could not fetch the ${key} (${res.status}); press Refresh and try again`);
        save(await res.blob(), files[key]);
      }
      const kit = {
        letter_id: r.letter_id,
        status: r.status,
        first_name: r.first_name,
        dedicated_to: r.dedicated_to,
        place: r.place,
        letter_text: r.letter_text,
        client_tz: r.client_tz,
        created_at: r.created_at,
        files,
        donations: (r.donations || []).map(({ year, num_trees, dedicated_to, payment_status, partner_url }) => ({
          year, num_trees, dedicated_to, payment_status, partner_url,
        })),
      };
      save(new Blob([JSON.stringify(kit, null, 2)], { type: "application/json" }), `letter-${id}.json`);
    } catch (err) {
      alert("Download failed: " + err.message);
    }
  };

  const editText = async (r, field, label, multiline) => {
    const v = window.prompt(label + (multiline ? " (line breaks: type \\n)" : ""), (r[field] || "").replace(/\n/g, "\\n"));
    if (v === null) return;
    await update(r.letter_id, { [field]: v.replace(/\\n/g, "\n") });
  };

  const shown = view === "archived" ? rows : rows.filter((r) => r.status === view);
  const counts = rows.reduce((c, r) => ({ ...c, [r.status]: (c[r.status] || 0) + 1 }), {});
  const isHeic = (r) => /^image\/hei[cf]$/.test(r.image_mime || "") || /\.hei[cf]$/.test(r.image_path);
  const fmt = (d) =>
    d ? new Date(d).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "";

  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["pending", "approved", "rejected", "archived"].map((v) => (
          <button
            key={v}
            onClick={() => setView(v)}
            className={`px-4 py-2 rounded-lg text-sm font-medium border ${
              view === v ? "bg-sritaru-600 text-white border-sritaru-600" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
            }`}
          >
            {v === "archived" ? "Archived" : STATUS_LABELS[v]}
            {view !== "archived" && v !== "archived" ? ` (${counts[v] || 0})` : ""}
          </button>
        ))}
        <button onClick={load} className="ml-auto px-4 py-2 rounded-lg text-sm border border-gray-300 bg-white hover:bg-gray-50">
          Refresh
        </button>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Only approved letters are used in reels and posts. Show only the first name publicly; email and WhatsApp stay private.
      </p>

      {loading ? (
        <div className="text-center py-12 text-gray-500">Loading...</div>
      ) : shown.length === 0 ? (
        <div className="text-center py-12 text-gray-500 bg-white rounded-xl shadow-md">No letters here.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {shown.map((r) => {
            const url = urls[r.image_path];
            return (
              <div key={r.letter_id} className="bg-white rounded-xl shadow-md overflow-hidden flex flex-col">
                <div className="bg-gray-100 h-72 flex items-center justify-center">
                  {url && !isHeic(r) ? (
                    <a href={url} target="_blank" rel="noopener noreferrer" className="h-full w-full">
                      <img src={url} alt={`Letter from ${r.first_name}`} className="h-full w-full object-contain" />
                    </a>
                  ) : (
                    <div className="text-center text-sm text-gray-500 px-4">
                      {isHeic(r) ? "HEIC photo (browsers other than Safari can't preview it)." : "Photo not available."}
                    </div>
                  )}
                </div>
                <div className="p-4 space-y-2 text-sm flex-grow">
                  <div className="flex items-center justify-between">
                    <p className="font-bold text-gray-800">
                      {r.first_name} {r.last_name || ""}
                    </p>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_STYLES[r.status]}`}>
                      {STATUS_LABELS[r.status]}
                    </span>
                  </div>
                  {r.dedicated_to && <p className="text-gray-700">Dedicated to: {r.dedicated_to}</p>}
                  <p className="text-gray-700">
                    Writing from: {r.place || <span className="text-gray-400">(not given)</span>}{" "}
                    <button onClick={() => editText(r, "place", "City shown in the corner stamp")} className="text-xs text-sritaru-600 hover:underline">
                      edit
                    </button>
                  </p>
                  <div className="text-xs">
                    <span className="text-gray-500">Typed letter: </span>
                    <button
                      onClick={() => editText(r, "letter_text", "The letter's words, as written", true)}
                      className="text-sritaru-600 hover:underline"
                    >
                      edit
                    </button>
                    {r.letter_text ? (
                      <p className="mt-1 whitespace-pre-wrap text-gray-700 max-h-28 overflow-y-auto bg-gray-50 rounded p-2">{r.letter_text}</p>
                    ) : (
                      <span className="text-gray-400"> (not given: type it in before making a reel)</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-600">
                    {(r.donations || []).length ? (
                      <>
                        Donations:{" "}
                        {r.donations
                          .map((d) => `${d.num_trees} tree${d.num_trees === 1 ? "" : "s"} (${d.year || "?"}${d.partner_url ? ", tree page" : ""})`)
                          .join(" · ")}
                      </>
                    ) : (
                      <span className="text-gray-400">No donation found with this email or WhatsApp.</span>
                    )}
                  </div>
                  {r.audio_path && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">
                        &#127908; Read aloud ({r.audio_source === "recorded" ? "recorded on the page" : "uploaded file"}
                        {r.audio_bytes ? `, ${(r.audio_bytes / 1024 / 1024).toFixed(1)} MB` : ""})
                      </p>
                      {urls[r.audio_path] ? (
                        <audio controls preload="none" src={urls[r.audio_path]} className="w-full h-9" />
                      ) : (
                        <p className="text-xs text-gray-400">Recording not available.</p>
                      )}
                    </div>
                  )}
                  <p className="text-gray-500 text-xs">
                    {[r.email, r.whatsapp].filter(Boolean).join(" · ")}
                  </p>
                  <p className="text-gray-400 text-xs">
                    Sent {fmt(r.created_at)}
                    {r.image_bytes ? ` · ${(r.image_bytes / 1024 / 1024).toFixed(1)} MB` : ""}
                    {r.consent_public && r.consent_own_words ? " · both consents ticked" : ""}
                  </p>
                  {r.archived_at && (
                    <p className="text-gray-500 text-xs">
                      Archived {fmt(r.archived_at)}
                      {r.archived_reason ? `: ${r.archived_reason}` : ""}
                    </p>
                  )}
                  <div className="flex gap-1">
                    <input
                      type="text"
                      value={notes[r.letter_id] ?? ""}
                      onChange={(e) => setNotes({ ...notes, [r.letter_id]: e.target.value })}
                      placeholder="Review note (private)"
                      className="flex-grow border border-gray-300 rounded px-2 py-1 text-xs"
                    />
                    {(notes[r.letter_id] ?? "") !== (r.review_note || "") && (
                      <button
                        onClick={() => update(r.letter_id, { review_note: notes[r.letter_id] })}
                        className="text-green-600 hover:text-green-800 text-xs font-bold px-1"
                      >
                        Save
                      </button>
                    )}
                  </div>
                </div>
                <div className="border-t border-gray-100 p-3 flex flex-wrap gap-2">
                  {view !== "archived" && (
                    <>
                      {r.status !== "approved" && (
                        <button
                          onClick={() => update(r.letter_id, { status: "approved" })}
                          className="bg-sritaru-600 text-white text-xs px-3 py-1.5 rounded hover:bg-sritaru-700"
                        >
                          Approve
                        </button>
                      )}
                      {r.status !== "rejected" && (
                        <button
                          onClick={() => update(r.letter_id, { status: "rejected" })}
                          className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded hover:bg-gray-50"
                        >
                          Reject
                        </button>
                      )}
                      {r.status !== "pending" && (
                        <button
                          onClick={() => update(r.letter_id, { status: "pending" })}
                          className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded hover:bg-gray-50"
                        >
                          Back to pending
                        </button>
                      )}
                    </>
                  )}
                  {url && (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded hover:bg-gray-50"
                    >
                      Original
                    </a>
                  )}
                  {r.status === "approved" && view !== "archived" && url && (
                    <button
                      onClick={() => downloadKit(r)}
                      className="bg-earth-500 text-white text-xs px-3 py-1.5 rounded hover:bg-earth-600"
                    >
                      Download for reel
                    </button>
                  )}
                  {urls[r.audio_path] && (
                    <a
                      href={urls[r.audio_path]}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="border border-gray-300 text-gray-700 text-xs px-3 py-1.5 rounded hover:bg-gray-50"
                    >
                      Audio
                    </a>
                  )}
                  {view === "archived" ? (
                    <button onClick={() => archive(r.letter_id, false)} className="ml-auto text-sritaru-600 hover:text-sritaru-800 text-xs">
                      Restore
                    </button>
                  ) : (
                    <button onClick={() => archive(r.letter_id, true)} className="ml-auto text-red-400 hover:text-red-600 text-xs">
                      Archive
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default AdminLetters;
